/**
 * Cutting a line of text short to fit its box (P1.9-T13; ADR-061, amendment "the export is a visual
 * reference": the message line grows to at most four lines and cuts a longer reply short with U+2026). The
 * box decides what fits, so the browser that lays the text out is the only thing that wraps it and the cut
 * can never disagree with the lines drawn; this module only searches for the longest start of the text that
 * still fits with the ellipsis after it.
 */

/**
 * The ellipsis a cut text ends with, U+2026, a glyph of both the pixel face and Noto Sans.
 */
export const ELLIPSIS = "…";

/**
 * A text's first characters, with any space they end on dropped and the ellipsis after them.
 *
 * @param points The text's code points.
 * @param count How many to keep.
 * @returns The cut text.
 */
function cut(points: readonly string[], count: number): string {
  return `${points.slice(0, count).join("").trimEnd()}${ELLIPSIS}`;
}

/**
 * The text as it fits its box: the whole text when it fits, otherwise its longest start that fits with the
 * ellipsis after it, cut between code points so no character written as a surrogate pair is split. The
 * search halves the range each time, so a 1,024-character sign asks the box about a dozen times; it assumes
 * that a start which fits keeps fitting when shortened, as wrapped text does, and it returns only a text the
 * box accepted, or the ellipsis alone, which is taken to fit any box.
 *
 * @param text The text.
 * @param fits Whether a candidate fits the box: for the message line, whether its box stays within four
 *   lines.
 * @returns The text to show.
 * @example
 * cutToFit("abcdefghijklmnop", (candidate) => candidate.length <= 10);
 * // "abcdefghi…"
 */
export function cutToFit(text: string, fits: (candidate: string) => boolean): string {
  if (fits(text)) {
    return text;
  }
  const points = Array.from(text);
  let kept = 0;
  let over = points.length;
  while (over - kept > 1) {
    const middle = Math.floor((kept + over) / 2);
    if (fits(cut(points, middle))) {
      kept = middle;
    } else {
      over = middle;
    }
  }
  return cut(points, kept);
}
